import { useCallback, useEffect, useState } from 'react';
import { UseFormGetValues } from 'react-hook-form';
import {
  IWebsiteGraderBusinessDetails,
  useWebsiteGraderContext,
  WebsiteGraderStep,
} from 'src/components/providers/websiteGrader';
import { Logger } from 'src/utils/Logger';
import { WebsiteGraderProcessScore } from '../Utils';
import { useNavigate } from 'react-router-dom';
import useGATracking from 'src/hooks/google/useGATracking';

const captchSrc = `https://www.google.com/recaptcha/api.js?render=${process.env.REACT_APP_RECAPTCHA_SITE_KEY}`;

type ReturnType = {
  disabled: boolean;
  onSubmit: () => void;
};

export const useSubmitBusinessDetailsForm = (
  getValues: UseFormGetValues<IWebsiteGraderBusinessDetails>
): ReturnType => {
  const [disabled, setDisabled] = useState(false);
  const context = useWebsiteGraderContext();
  const navigate = useNavigate();
  const { pushCustomGAEvent } = useGATracking({ triggerOnMount: false });

  useEffect(() => {
    const script = document.createElement('script');
    script.src = captchSrc;
    document.body.appendChild(script);
  }, []);

  const onSubmit = useCallback(() => {
    setDisabled(true);
    const websiteGraderBusinessDetails = (
      ['businessName', 'website', 'businessType'] as (keyof IWebsiteGraderBusinessDetails)[]
    ).reduce(
      (userDetails, fieldName) => ({
        ...userDetails,
        [fieldName]: getValues(fieldName),
      }),
      {} as IWebsiteGraderBusinessDetails
    );
    Logger.trace(
      'onSubmit:websiteGraderBusinessDetails',
      { websiteGraderBusinessDetails },
      'useSubmitBusinessDetailsForm'
    );

    context.setBusinessDetails({ ...websiteGraderBusinessDetails });
    context.setCurrentStep(WebsiteGraderStep.LOADING_RESULTS);

    pushCustomGAEvent({
      gaEventCategory: 'WebsiteGrader',
      gaEventAction: 'Submit',
      gaEventLabel: 'WebsiteGraderBusinessDetails',
      buttonId: 'website-grader-collect-business-details-next',
    });

    window.grecaptcha.ready(() => {
      window.grecaptcha.execute(process.env.REACT_APP_RECAPTCHA_SITE_KEY!, { action: 'submit' }).then((recaptcha) => {
        WebsiteGraderProcessScore({ ...context.userDetails, ...websiteGraderBusinessDetails, recaptcha })
          .then((response) => navigate(`/website-grader/report/${response.uuid}`))
          .catch((error) => {
            context.setError(error);
            context.setCurrentStep(WebsiteGraderStep.ERROR);
          });
      });
    });
  }, [context]);

  return { disabled, onSubmit };
};
